// src/contexts/CurrencyContext.js
// 🆕 [MULTI-DEVISES] Contexte global de la devise utilisateur
// Style aligné sur NetworkContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  fetchCurrencies,
  fetchMyCurrency,
  updateMyCurrency,
} from '../services/currencyService';
import { formatCurrency, formatDual, formatRate } from '../utils/formatCurrency';

const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency doit être utilisé dans <CurrencyProvider>');
  return ctx;
};

export const CurrencyProvider = ({ children }) => {
  const [currencies, setCurrencies] = useState([]);
  const [balanceUSDT, setBalanceUSDT] = useState(0);
  const [display, setDisplay] = useState(null); // { amount, currency, formatted, rate, ... }
  const [currencySource, setCurrencySource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Charge la liste des devises (une seule fois)
  const loadCurrencies = useCallback(async () => {
    try {
      const data = await fetchCurrencies();
      if (data?.success) {
        setCurrencies(data.currencies || []);
      }
    } catch (err) {
      console.error('[CurrencyContext] loadCurrencies →', err.message);
    }
  }, []);

  // Charge la devise effective + solde
  const loadMyCurrency = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const data = await fetchMyCurrency();
      if (data?.success) {
        setBalanceUSDT(data.balanceUSDT || 0);
        setDisplay(data.display || null);
        setCurrencySource(data.currencySource || null);
      }
    } catch (err) {
      console.error('[CurrencyContext] loadMyCurrency →', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Change la devise manuellement
  const changeCurrency = useCallback(async (code) => {
    setLoading(true);
    setError(null);
    try {
      const data = await updateMyCurrency(code);
      if (data?.success) {
        setBalanceUSDT(data.balanceUSDT || 0);
        setDisplay(data.display || null);
        setCurrencySource('manual');
        return { success: true };
      }
      return { success: false, error: data?.message || 'Erreur inconnue' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Au montage : charge devises + devise user
  useEffect(() => {
    loadCurrencies();
    loadMyCurrency();
  }, [loadCurrencies, loadMyCurrency]);

  // Récupère le code de la devise effective
  const effectiveCode = display?.currency || 'USDT';

  // Récupère l'objet currency complet (depuis la liste)
  const effectiveCurrency = currencies.find(c => c.code === effectiveCode) || null;

  const value = {
    // State
    currencies,
    balanceUSDT,
    display,
    currencySource,
    loading,
    error,
    effectiveCode,
    effectiveCurrency,

    // Actions
    loadCurrencies,
    loadMyCurrency,
    changeCurrency,

    // Formatters (helpers prêts à l'emploi)
    format: (amount) => formatCurrency(amount, effectiveCurrency),
    formatDual: () => formatDual(balanceUSDT, display),
    formatRate: (rate) => formatRate(rate, effectiveCurrency),
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};